import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Contact } from "@/components/site/Contact";
import { Reveal } from "@/components/site/Reveal";
import { VajacialTestimonials } from "@/components/site/VajacialTestimonials";
import { Dot } from "@/components/site/DotText";
import { VAJACIAL, PLANITY_URL } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";

export default function Vajacial() {
  useSeo({
    title: "Le Vajacial — Ninaa Beauty",
    description:
      "Le Vajacial, un soin ciblé de la zone intime chez Ninaa Beauty à Toulouse : poils incarnés, hyperpigmentation, marques d'épilation et confort de la peau.",
    path: "/vajacial",
  });

  return (
    <div className="App" data-testid="vajacial-page">
      <Header />
      <main className="page-enter bg-white">
        <div className="max-w-4xl mx-auto px-6 py-14 md:py-20">
          <Link
            to="/prestations/epilations"
            data-testid="back-to-epilations"
            className="inline-flex items-center gap-2 text-sm text-[#4b2018] hover:text-[#b07a63] transition-colors mb-10"
          >
            <ArrowLeft size={16} /> Retour aux épilations
          </Link>

          <Reveal>
            <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-[#723827] mb-5">
                  Soin dédié
                </p>
                <h1
                  data-testid="vajacial-title"
                  className="font-serif-display font-light text-[#4b2018] text-4xl md:text-5xl leading-[1]"
                >
                  {VAJACIAL.title}
                </h1>
                <p className="mt-4 font-serif-display text-2xl md:text-3xl text-[#7a5230]">
                  {VAJACIAL.subtitle}
                </p>
                <p className="mt-6 text-[#4b2018] leading-relaxed">
                  {VAJACIAL.intro}
                </p>
                {VAJACIAL.included && (
                  <div className="mt-5 flex flex-col items-start gap-2">
                    <span
                      data-testid="vajacial-included"
                      className="inline-flex items-center gap-2 rounded-full bg-[#a04a3a]/[0.22] text-[#632c1e] px-4 py-1.5 text-xs tracking-wide"
                    >
                      <Check size={14} /> {VAJACIAL.included}
                    </span>
                    {VAJACIAL.periodNote && (
                      <span
                        data-testid="vajacial-period-note"
                        className="inline-flex items-center gap-2 rounded-full bg-[#a04a3a]/[0.22] text-[#632c1e] px-4 py-1.5 text-xs tracking-wide"
                      >
                        <Check size={14} /> {VAJACIAL.periodNote}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {VAJACIAL.image && (
                <div className="overflow-hidden rounded-md order-first md:order-last">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={VAJACIAL.image}
                      alt="Le Vajacial — Ninaa Beauty"
                      data-testid="vajacial-image"
                      loading="eager"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          </Reveal>

          {/* Ce soin comprend */}
          <Reveal className="mt-12 md:mt-14">
            <div className="rounded-md border border-[#a04a3a]/45 bg-[#a04a3a]/[0.16] p-6 md:p-8 max-w-xl">
              <h2 className="font-serif-display font-light text-[#4b2018] text-2xl md:text-3xl mb-4">
                Ce soin comprend
              </h2>
              <p className="text-sm leading-relaxed text-[#4b2018]">
                {VAJACIAL.includes}
              </p>
            </div>
          </Reveal>

          {/* Idéal en cas de : */}
          <Reveal className="mt-10 md:mt-12">
            <h2 className="font-serif-display font-light text-[#4b2018] text-2xl md:text-3xl mb-6">
              Idéal en cas de&nbsp;:
            </h2>
            <ul className="space-y-3 max-w-xl">
              {VAJACIAL.concerns.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 text-[#4b2018]"
                >
                  <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-[#a04a3a]/[0.22] text-[#632c1e] shrink-0">
                    <Check size={14} />
                  </span>
                  <span className="text-sm leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Durée & tarif — sobre, harmonisé avec le reste du site */}
          {VAJACIAL.duration && VAJACIAL.price && (
            <Reveal className="mt-8 md:mt-10">
              <p
                data-testid="vajacial-duration-price"
                className="text-sm tracking-wide text-[#723827]"
              >
                {VAJACIAL.duration}
                <Dot />
                {VAJACIAL.price}
              </p>
            </Reveal>
          )}

          {/* Leurs expériences — carrousel de témoignages */}
          {VAJACIAL.testimonials && VAJACIAL.testimonials.length > 0 && (
            <Reveal className="mt-14 md:mt-20">
              <VajacialTestimonials items={VAJACIAL.testimonials} />
            </Reveal>
          )}

          <Reveal className="mt-14 md:mt-20">
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="vajacial-page-reserve-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#4e231b] text-white px-7 py-3.5 text-sm hover:bg-[#b07a63] transition-colors duration-400"
            >
              Réserver le Vajacial <ArrowRight size={16} />
            </a>
          </Reveal>
        </div>
      </main>
      <Contact />
    </div>
  );
}
